import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkles, Send, Loader2, MessageCircle } from "lucide-react";
import { useCustomerAuth } from "../../contexts/CustomerAuthContext";
import { useDocMeta } from "../../lib/useDocMeta";

/**
 * Customer AI Copilot — Phase 3
 * Rule-based, scoped to a single owned report. Backend endpoint:
 *   POST /api/customer/copilot/ask { analysis_id, question }
 *
 * Designed so the rule engine can be swapped for an LLM later without
 * UI changes (request shape and response shape are stable).
 */
const SUGGESTIONS = [
  "Explain this score",
  "Summarize this report",
  "What risks matter most?",
  "What should I review next?",
  "What does Go/No-Go mean here?",
  "Which Learning Center topics apply?",
];

export default function CustomerCopilot() {
  useDocMeta({ title: "AI Copilot · GovBidIQ", path: "/app/copilot" });
  const { custApi } = useCustomerAuth();
  const [params, setParams] = useSearchParams();
  const initialId = params.get("id") || "";
  const [reports, setReports] = useState(null);
  const [activeId, setActiveId] = useState(initialId);
  const [history, setHistory] = useState([]); // {q, a, deny}
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    custApi.get("/customer/reports").then((r) => {
      setReports(r.data);
      if (!initialId && r.data?.length) setActiveId(r.data[0].id);
    }).catch(() => setReports([]));
  }, []);

  useEffect(() => {
    if (activeId) setParams({ id: activeId }, { replace: true });
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history.length]);

  const activeReport = useMemo(
    () => (reports || []).find((r) => r.id === activeId) || null,
    [reports, activeId],
  );

  const ask = async (text) => {
    const question = (text ?? q).trim();
    if (!question || !activeId) return;
    setBusy(true);
    setHistory((h) => [...h, { q: question, a: "…", pending: true }]);
    setQ("");
    try {
      const r = await custApi.post("/customer/copilot/ask", { analysis_id: activeId, question });
      setHistory((h) => h.slice(0, -1).concat({ q: question, a: r.data.answer, deny: r.data.deny }));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not get an answer right now.";
      setHistory((h) => h.slice(0, -1).concat({ q: question, a: msg, error: true }));
    } finally {
      setBusy(false);
    }
  };

  if (reports === null) {
    return <div className="text-slate-500 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  if (reports.length === 0) {
    return (
      <div data-testid="cust-copilot-empty" className="max-w-xl">
        <div className="tick-label text-sky-400 mb-1">/ AI Copilot</div>
        <h1 className="font-display font-black text-3xl text-slate-100 mb-3">No report to discuss yet</h1>
        <p className="text-slate-400 text-sm mb-6">Copilot answers are scoped to a specific report you own. Once you have a report, come back here.</p>
        <Link to="/sample" className="border border-sky-500/60 text-sky-300 hover:bg-sky-500/10 font-mono text-[11px] tracking-widest uppercase px-3 py-2 inline-block">View Sample Report</Link>
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-4 max-w-[1100px]" data-testid="cust-copilot">
      <aside className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] p-3 max-h-[70vh] overflow-auto">
        <div className="tick-label text-sky-400 mb-2">/ Choose a report</div>
        <ul className="space-y-1">
          {reports.map((r) => (
            <li key={r.id}>
              <button
                onClick={() => { setActiveId(r.id); setHistory([]); }}
                data-testid={`cust-copilot-pick-${r.id}`}
                className={`w-full text-left px-2 py-1.5 text-xs leading-snug ${activeId === r.id ? "bg-sky-500/10 text-sky-200 border-l-2 border-sky-500" : "text-slate-300 hover:text-slate-100"}`}
              >
                <div className="truncate font-mono">{r.opportunity_name || r.document_name || r.id}</div>
                <div className="text-[10px] text-slate-500 tracking-widest uppercase">{r.final_verdict || r.status}</div>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="border border-[var(--border-soft)] bg-[var(--bg-secondary)] flex flex-col min-h-[60vh]">
        <header className="border-b border-[var(--border-soft)] p-4">
          <div className="tick-label text-sky-400 mb-1">/ AI Copilot</div>
          <h1 className="font-display font-bold text-xl text-slate-100 inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-sky-400" />
            {activeReport?.opportunity_name || activeReport?.document_name || "Pick a report"}
          </h1>
          <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
            Rule-based assistant. Explains scores, risks, and recommendations. Cannot give legal, financial, or award-guarantee advice.
          </p>
        </header>

        <div ref={scrollRef} className="flex-1 p-4 space-y-3 overflow-auto" data-testid="cust-copilot-transcript">
          {history.length === 0 && (
            <div className="text-xs text-slate-500 italic">Try one of the suggestions below to get started.</div>
          )}
          {history.map((h, i) => (
            <div key={i} className="space-y-1">
              <div className="text-[11px] font-mono tracking-widest uppercase text-slate-500">You</div>
              <div className="text-slate-200 text-sm">{h.q}</div>
              <div className="text-[11px] font-mono tracking-widest uppercase text-sky-400 mt-2">Copilot</div>
              <div className={`text-sm leading-relaxed whitespace-pre-wrap ${h.error ? "text-rose-300" : h.deny ? "text-amber-300" : "text-slate-100"}`} data-testid={`cust-copilot-answer-${i}`}>
                {h.pending ? <Loader2 className="w-4 h-4 animate-spin inline" /> : h.a}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-[var(--border-soft)] p-3 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => ask(s)} disabled={busy || !activeId} data-testid={`cust-copilot-suggestion-${s.replace(/\s+/g,'-').toLowerCase()}`}
                className="border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 disabled:opacity-40 font-mono text-[10px] tracking-widest uppercase px-2 py-1">
                {s}
              </button>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask about this report…" data-testid="cust-copilot-input"
              className="flex-1 bg-slate-950 border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-3 py-2 font-mono" />
            <button type="submit" disabled={busy || !activeId || !q.trim()} data-testid="cust-copilot-send"
              className="bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-slate-950 font-mono text-[11px] tracking-widest uppercase px-3 py-2 inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
            </button>
          </form>
          <div className="text-[10px] text-slate-500 font-mono inline-flex items-center gap-1">
            <MessageCircle className="w-3 h-3" /> Answers scoped to <span className="text-sky-400">{activeReport?.id?.slice(0, 8) || "—"}</span>. No legal or financial advice.
          </div>
        </div>
      </section>
    </div>
  );
}
